import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { createEnrollment, updateMe } from '../../api/client';
import { trackEnrollmentFunnel } from '../../hooks/useTracking';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import PageHeader from '../../components/layout/PageHeader';
import {
  User,
  Phone,
  Calendar,
  Building2,
  GraduationCap,
  Briefcase,
  IndianRupee,
  MessageSquare,
  LinkIcon,
  ArrowRight,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  Tag,
  VenusAndMars,
} from 'lucide-react';

const steps = [
  { id: 'personal', label: 'Personal', icon: User },
  { id: 'professional', label: 'Professional', icon: Briefcase },
  { id: 'additional', label: 'Additional', icon: MessageSquare },
];

export default function EnrollmentFormScreen(): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const course = location.state?.course;

  // Auto-apply coupon from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const couponFromUrl = urlParams.get('coupon');
    if (couponFromUrl) {
      setForm((prev) => ({ ...prev, coupon_code: couponFromUrl }));
    }
  }, []);

  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState({
    mobile: user?.mobile || '',
    dob: user?.dob || '',
    gender: user?.gender || '',
    company: user?.company || '',
    education: user?.education || '',
    completion_year: user?.completion_year || '',
    current_status: user?.current_status || '',
    current_salary: user?.current_salary || '',
    course_expectations: user?.course_expectations || '',
    linkedin: user?.linkedin || '',
    coupon_code: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (course) {
      trackEnrollmentFunnel('start', { courseId: course.id });
    }
  }, [course]);

  const handleChange = (key: string, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => { const next = { ...prev }; delete next[key]; return next; });
    }
  };

  const validateStep = () => {
    const newErrors: Record<string, string> = {};
    if (currentStep === 0) {
      if (!form.mobile.trim()) newErrors.mobile = 'Mobile number is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
      trackEnrollmentFunnel('step_complete', { step: currentStep + 1, courseId: course?.id });
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    try {
      setLoading(true);

      // Step A: Update user profile with all enrollment form data
      await updateMe({
        mobile: form.mobile,
        dob: form.dob,
        gender: form.gender,
        company: form.company,
        education: form.education,
        completion_year: form.completion_year,
        current_status: form.current_status,
        current_salary: form.current_salary,
        course_expectations: form.course_expectations,
        linkedin: form.linkedin,
      });

      // Step B: Create enrollment
      trackEnrollmentFunnel('submit', { courseId: course.id });
      const res = await createEnrollment({
        course_id: course.id,
        ...form,
      });

      if (res.payment_required) {
        navigate('/payment', { state: { course, amount: res.amount, coupon_applied: res.coupon_applied } });
      } else {
        toast.show(res.message || 'Enrolled successfully!', 'success');
        navigate('/');
      }
    } catch (error: any) {
      toast.show(error.message || 'Enrollment failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-600">
        No course selected
      </div>
    );
  }

  // const StepIcon = steps[currentStep].icon;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 animate-fade-in">
      <PageHeader
        title="Enroll in Course"
        subtitle={course.title}
      />

      {/* Progress steps */}
      <div className="flex items-center gap-2 mb-8">
        {steps.map((step, idx) => {
          const isActive = idx === currentStep;
          const isCompleted = idx < currentStep;
          const StepIcon = step.icon;
          return (
            <React.Fragment key={step.id}>
              <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                isActive
                  ? 'bg-amber-500 text-white shadow-md shadow-amber-500/20'
                  : isCompleted
                  ? 'bg-green-50 text-green-700'
                  : 'bg-slate-100 text-slate-400'
              }`}>
                {isCompleted ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <StepIcon className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">{step.label}</span>
              </div>
              {idx < steps.length - 1 && (
                <ChevronRight className="w-4 h-4 text-slate-300" />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Form */}
      <motion.div
        key={currentStep}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-100 shadow-sm"
      >
        <AnimatePresence mode="wait">
          {currentStep === 0 && (
            <motion.div key="personal" className="space-y-5">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Personal Information</h3>
              <Input
                label="Mobile Number"
                value={form.mobile}
                onChange={e => handleChange('mobile', e.target.value)}
                placeholder="Enter your mobile number"
                icon={<Phone className="w-4 h-4" />}
                error={errors.mobile}
                required
              />
              <Input
                label="Date of Birth"
                type="date"
                value={form.dob}
                onChange={e => handleChange('dob', e.target.value)}
                icon={<Calendar className="w-4 h-4" />}
              />
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Gender</label>
                <div className="relative">
                  <VenusAndMars className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <select
                    value={form.gender}
                    onChange={e => handleChange('gender', e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 appearance-none"
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                    <option value="prefer_not_to_say">Prefer not to say</option>
                  </select>
                </div>
              </div>
              <Input
                label="LinkedIn Profile"
                type="url"
                value={form.linkedin}
                onChange={e => handleChange('linkedin', e.target.value)}
                placeholder="https://linkedin.com/in/..."
                icon={<LinkIcon className="w-4 h-4" />}
              />
            </motion.div>
          )}

          {currentStep === 1 && (
            <motion.div key="professional" className="space-y-5">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Professional Details</h3>
              <Input
                label="Organization / College Name"
                value={form.company}
                onChange={e => handleChange('company', e.target.value)}
                placeholder="Company or college name"
                icon={<Building2 className="w-4 h-4" />}
              />
              <Input
                label="Education Institution"
                value={form.education}
                onChange={e => handleChange('education', e.target.value)}
                placeholder="Institution name"
                icon={<GraduationCap className="w-4 h-4" />}
              />
              <Input
                label="Completion Year"
                type="number"
                value={form.completion_year}
                onChange={e => handleChange('completion_year', e.target.value)}
                placeholder="e.g. 2024"
                icon={<Calendar className="w-4 h-4" />}
              />
            </motion.div>
          )}

          {currentStep === 2 && (
            <motion.div key="additional" className="space-y-5">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Additional Information</h3>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Profession</label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <select
                    value={form.current_status}
                    onChange={e => handleChange('current_status', e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 appearance-none"
                  >
                    <option value="">Select your profession</option>
                    <option value="Student">Student</option>
                    <option value="Employee">Working Professional</option>
                    <option value="Entrepreneur">Entrepreneur</option>
                  </select>
                </div>
              </div>
              <Input
                label="Current Salary"
                value={form.current_salary}
                onChange={e => handleChange('current_salary', e.target.value)}
                placeholder="e.g. 5 LPA"
                icon={<IndianRupee className="w-4 h-4" />}
              />
              <Input
                label="Course Expectations"
                multiline
                value={form.course_expectations}
                onChange={e => handleChange('course_expectations', e.target.value)}
                placeholder="What do you expect from this course?"
                icon={<MessageSquare className="w-4 h-4" />}
              />
              <Input
                label="Coupon Code (Optional)"
                value={form.coupon_code}
                onChange={e => handleChange('coupon_code', e.target.value)}
                placeholder="Enter coupon code if you have one"
                icon={<Tag className="w-4 h-4" />}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={currentStep === 0}
            leftIcon={<ChevronLeft className="w-4 h-4" />}
          >
            Back
          </Button>

          {currentStep < steps.length - 1 ? (
            <Button onClick={handleNext} rightIcon={<ChevronRight className="w-4 h-4" />}>
              Continue
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              isLoading={loading}
              rightIcon={<ArrowRight className="w-4 h-4" />}
            >
              Complete Enrollment
            </Button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
